import { CognitoUser } from "amazon-cognito-identity-js";
import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import styled from "styled-components";

import {
  Button,
  Form,
  FormContext,
  inputGlow,
  Link,
  RouterLink,
  TextInput,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";

import { currentAuthenticatedUser } from "../../auth";
import { useLogout } from "../../hooks/useLogout";
import { useAppContext } from "../../providers";
import AppLoading from "../AppLoading";

import LoginCampfireNeoApps from "./LoginCampfireNeoApps";
import { useLogin } from "./useLogin";

const Styled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  select {
    ${inputGlow}
    appearance: none;
    background-color: #fff;
    border-radius: 0.3rem;
    border: solid 1px #ddd;
    box-sizing: border-box;
    font-family: inherit;
    font-size: inherit;
    line-height: 3.5rem;
    outline: none;
    padding: 0 1.5rem;
    width: 100%;
  }

  input {
    height: 3.5rem;
    font-size: 14px;
    padding: 1rem 1.5rem;
  }

  button {
    height: 3.5rem;
    font-size: 14px;
  }

  h1 {
    font-weight: 300;
    /* text-transform: uppercase; */
    color: #555;
    margin-bottom: 4rem;
    text-align: center;
  }
`;

interface Props {
  header?: React.ReactNode;
  onLogin: (data: CognitoUser) => void;
  allowRegistration?: boolean;
  onRegisterButtonClick?: () => void;
  onForgotPasswordButtonClick?: () => void;
  onLanguageChange?: (value: string) => void;
  loginWithUrl?: string;
  loginWithText?: string;
}

const LoginCampfireNeo = (props: Props) => {
  const [appState] = useAppContext();
  const logout = useLogout();
  const {
    login,
    isLoggingIn,
    workspaces,
    reset,
    selectWorkspace,
    processSignInResult,
  } = useLogin({
    onLogin: props.onLogin,
  });
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [needsAuthentication, setNeedsAuthentication] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  const handleLoginSubmit = async () => {
    login(username, password);
  };

  const appStateUserId = appState.user?.id;
  const appId = appState.app?.id;
  const { onLogin } = props;
  useEffect(() => {
    currentAuthenticatedUser().then((user) => {
      if (user == null || appStateUserId == null) {
        setNeedsAuthentication(true);
        return;
      }

      if (appState.workspace?.id == null || appState.app?.id == null) {
        //
        // @todo this is a bit of a hack
        // users logging in with SSO will not have a workspaceId necessarily
        // so we need to trigger the logic inside useLogin that loads up
        // their available ones
        //
        processSignInResult(user);
      } else {
        onLogin(user);
      }
    });
  }, [
    appId,
    appState.app?.id,
    appState.workspace?.id,
    appStateUserId,
    onLogin,
    processSignInResult,
  ]);

  return (
    <Styled>
      {props.header}
      {workspaces !== undefined && (
        <LoginCampfireNeoApps
          options={workspaces}
          onBack={() => {
            logout().then(() => {
              reset();
            });
          }}
          onRegister={props.onRegisterButtonClick}
          onSelect={(workspace) => {
            selectWorkspace(workspace);
          }}
        />
      )}
      {workspaces === undefined &&
        (!needsAuthentication ? (
          <AppLoading text={`Logging in ${username}`} />
        ) : (
          <FormContext.Provider value={{ disabled: isLoggingIn }}>
            <Form>
              <Form.FieldList>
                <Form.Field label="Username">
                  <TextInput
                    ref={ref}
                    autoCapitalize="off"
                    autoFocus
                    type="email"
                    name="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onEnterKeyPress={handleLoginSubmit}
                  />
                </Form.Field>
                <Form.Field label="Password">
                  <TextInput
                    type="password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onEnterKeyPress={handleLoginSubmit}
                  />
                </Form.Field>
                <VSpace style={{ marginTop: "2rem" }}>
                  <Toolbar>
                    {props.allowRegistration && (
                      <Button
                        data-cy="login-register"
                        title="Register"
                        variant="contained"
                        stretch
                        onClick={props.onRegisterButtonClick}
                      >
                        Register
                      </Button>
                    )}
                    <Button
                      data-cy="login-submit"
                      title="Login"
                      color="primary"
                      variant="contained"
                      stretch
                      onClick={(e) => {
                        e.preventDefault();
                        handleLoginSubmit();
                      }}
                      isLoading={isLoggingIn}
                      disabled={
                        username.length === 0 || password.length === 0
                          ? true
                          : undefined
                      }
                    >
                      <FormattedMessage
                        id="login"
                        defaultMessage="Login"
                        values={{ ts: Date.now() }}
                      />
                    </Button>
                  </Toolbar>
                  <Toolbar>
                    <RouterLink to="/login/reset">Forgot Password</RouterLink>
                    {props.loginWithUrl && (
                      <Toolbar.Right>
                        <Link href={props.loginWithUrl}>
                          {props.loginWithText}
                        </Link>
                      </Toolbar.Right>
                    )}
                  </Toolbar>
                </VSpace>
              </Form.FieldList>
            </Form>
          </FormContext.Provider>
        ))}
    </Styled>
  );
};

export default LoginCampfireNeo;
